// Tile images (FluffyStuff riichi-mahjong-tiles) and keyed DOM rendering for hands and rivers.
import { TILES, tileName, tileLabel } from '../game.js';

const SUIT_FILES = { m: 'Man', p: 'Pin', s: 'Sou' };
const HONOR_FILES = ['', 'Ton', 'Nan', 'Shaa', 'Pei', 'Haku', 'Hatsu', 'Chun'];
export const tileSrc = (t) => `./tiles/${t[0] === 'z' ? HONOR_FILES[+t[1]] : SUIT_FILES[t[0]] + t[1]}.svg`;
const ALL = TILES.map(tileSrc);

// Preload every tile so the table never flashes blank tiles mid-game.
export function createTileLoader({ ImageClass = window.Image, onProgress = () => {}, timeoutMs = 15000 } = {}) {
  const loaded = new Map();
  let pending = null;
  const state = { status: 'loading', loaded: 0, total: ALL.length };

  function loadOne(src) {
    return new Promise((resolve, reject) => {
      const img = new ImageClass();
      let done = false;
      const finish = (err) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        img.onload = img.onerror = null;
        if (err) return reject(err);
        loaded.set(src, img);
        state.loaded = loaded.size;
        onProgress();
        resolve();
      };
      const timer = setTimeout(() => finish(new Error('Tile image timed out')), timeoutMs);
      img.decoding = 'async';
      img.onload = () => (typeof img.decode === 'function' ? img.decode().then(() => finish(), finish) : finish());
      img.onerror = () => finish(new Error('Tile image unavailable'));
      img.src = src;
    });
  }

  function load() {
    if (pending) return pending;
    if (state.status === 'ready') return Promise.resolve(true);
    state.status = 'loading';
    onProgress();
    const queue = ALL.filter((s) => !loaded.has(s));
    pending = Promise.all(
      Array.from({ length: 6 }, async () => {
        while (queue.length) {
          try {
            await loadOne(queue.shift());
          } catch {}
        }
      }),
    )
      .then(() => {
        state.status = loaded.size === ALL.length ? 'ready' : 'error';
        onProgress();
        return state.status === 'ready';
      })
      .finally(() => {
        pending = null;
      });
    return pending;
  }

  return { state, load };
}

export function setAttr(el, name, value) {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== String(value)) el.setAttribute(name, String(value));
}

function setTileImage(el, t) {
  let img = el.querySelector('img');
  if (!img) {
    img = el.ownerDocument.createElement('img');
    img.draggable = false;
    el.prepend(img);
  }
  setAttr(img, 'src', tileSrc(t));
  setAttr(img, 'alt', tileName(t));
  setAttr(el, 'data-t', t);
  setAttr(el, 'data-label', tileLabel(t));
}

// Reuses buttons by key so taps aren't lost when the hand re-renders mid-press.
export function renderButtons(container, items) {
  const existing = new Map([...container.children].map((el) => [el.dataset.renderKey, el]));
  let cursor = container.firstElementChild;
  for (const item of items) {
    const el = existing.get(item.key) || container.ownerDocument.createElement('button');
    existing.delete(item.key);
    setAttr(el, 'data-render-key', item.key);
    setAttr(el, 'class', item.classes);
    for (const [k, v] of Object.entries(item.attributes)) setAttr(el, k, v);
    setTileImage(el, item.tile);
    const check = el.querySelector('.tile-selection-check');
    if (item.selected && !check) {
      const mark = container.ownerDocument.createElement('span');
      mark.className = 'tile-selection-check';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '✓';
      el.append(mark);
    } else if (!item.selected && check) check.remove();
    if (el !== cursor) container.insertBefore(el, cursor);
    cursor = el.nextElementSibling;
  }
  for (const el of existing.values()) el.remove();
}

const riverKeys = new WeakMap();
export function renderRiver(container, river, latest) {
  const key = JSON.stringify([latest, river.map((r) => [r.tile, r.riichi, r.called])]);
  if (riverKeys.get(container) === key) return;
  riverKeys.set(container, key);
  const stick = container.children.length !== river.length && container.scrollHeight - container.scrollTop - container.clientHeight < 12;
  river.forEach((r, i) => {
    let slot = container.children[i];
    if (!slot) {
      slot = container.ownerDocument.createElement('span');
      slot.append(container.ownerDocument.createElement('span'));
      container.append(slot);
    }
    setAttr(slot, 'class', `river-slot${r.riichi ? ' riichi-slot' : ''}`);
    setAttr(slot, 'aria-label', r.riichi ? 'Riichi declaration tile' : null);
    const tile = slot.firstElementChild;
    setAttr(tile, 'class', `tile${r.called ? ' called' : ''}${r.riichi ? ' reach-tile' : ''}${latest && i === river.length - 1 ? ' latest' : ''}`);
    setAttr(tile, 'title', tileName(r.tile));
    setAttr(tile, 'aria-label', `${r.riichi ? 'Riichi declaration: ' : ''}${r.called ? 'called ' : ''}${tileName(r.tile)}`);
    setTileImage(tile, r.tile);
  });
  while (container.children.length > river.length) container.lastElementChild.remove();
  if (stick) container.scrollTop = container.scrollHeight;
}
